import { useEffect, useRef, useState } from "react";
import { ICustomerAddress } from "../models/ICustomerAddress";
import { ICityWithTowns } from "../models/ICityList";
import { getCityListPublic } from "../services/city";
import {
  addCustomerAddress,
  updateCustomerAddress,
} from "../services/customerAddress";
import { getUserToken } from "../utils/tokenUtils";
import AddressMap from "../components/AddressMap";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  address: ICustomerAddress | null;
  onSuccess: () => void;
  // Yeni adres formunda "Varsayılan adres" anahtarı açık başlasın (ilk adres eklenirken)
  defaultIsDefault?: boolean;
}

type Status = "form" | "saving" | "success";

function AdresimPopup({ address, onSuccess, defaultIsDefault = false }: Props) {
  const { t } = useLanguage();
  const isEdit = address !== null;
  const closeRef = useRef<HTMLButtonElement>(null);

  const [cities, setCities] = useState<ICityWithTowns[]>([]);
  const [cityId, setCityId] = useState("");
  const [townId, setTownId] = useState("");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");
  const [isDefault, setIsDefault] = useState(defaultIsDefault);
  const [status, setStatus] = useState<Status>("form");
  const [error, setError] = useState("");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [mapVisible, setMapVisible] = useState(false);
  // Harita "Konum seçildi" rozetini gösterdiği sürece true. Kullanıcı haritayı
  // onaylanan noktadan uzaklaştırırsa false olur ve Düzelt/Kaydet kilitlenir —
  // yeniden "Haritadaki Konumu Onayla" basılmadan eski (veya yanlış) konum kaydedilemez.
  const [locationConfirmed, setLocationConfirmed] = useState(false);

  useEffect(() => {
    // Public uc; modal misafir kullanicida da mount oluyor.
    getCityListPublic().then((res) => {
      const sorted = res.data
        .sort((a, b) => a.cityNameTr.localeCompare(b.cityNameTr, "tr"))
        .map((c) => ({
          ...c,
          town: [...c.town].sort((a, b) => a.townNameTr.localeCompare(b.townNameTr, "tr")),
        }));
      setCities(sorted);
    }).catch(() => setCities([]));
  }, []);

  // Haritayı sadece modal tamamen açıldıktan sonra render et.
  // Böylece container boyutu 0x0 olmaz, Google Maps doğru init olur.
  useEffect(() => {
    const modalEl = document.getElementById("adresimModal");
    if (!modalEl) return;
    const onShown = () => setMapVisible(true);
    const onHide = () => setMapVisible(false);
    modalEl.addEventListener("shown.bs.modal", onShown);
    modalEl.addEventListener("hide.bs.modal", onHide);
    return () => {
      modalEl.removeEventListener("shown.bs.modal", onShown);
      modalEl.removeEventListener("hide.bs.modal", onHide);
    };
  }, []);

  useEffect(() => {
    if (address) {
      setCityId(address.cityId);
      setTownId(address.townId);
      setDescription(address.customerAddressDescription ?? "");
      setNote(address.customerAddressNote ?? "");
      setIsDefault(address.customerAddressIsDefault);
      const hasCoords =
        typeof address.customerAddressLatitude === "number" &&
        typeof address.customerAddressLongitude === "number" &&
        (address.customerAddressLatitude !== 0 || address.customerAddressLongitude !== 0);
      setLat(hasCoords ? address.customerAddressLatitude : null);
      setLng(hasCoords ? address.customerAddressLongitude : null);
      setLocationConfirmed(hasCoords);
    } else {
      setCityId("");
      setTownId("");
      setDescription("");
      setNote("");
      setIsDefault(defaultIsDefault);
      setLat(null);
      setLng(null);
      setLocationConfirmed(false);
    }
    setStatus("form");
    setError("");
  }, [address]);

  const selectedCity = cities.find((c) => c.id === cityId);
  const selectedTown = selectedCity?.town.find((t) => t.id === townId);
  const towns = selectedCity?.town ?? [];

  const handleCityChange = (newCityId: string) => {
    setCityId(newCityId);
    setTownId("");
    setLat(null);
    setLng(null);
    setLocationConfirmed(false);
  };

  const handleSubmit = async () => {
    if (!cityId || !townId || !description.trim() || !note.trim()) {
      setError(t("addressForm.requiredFields"));
      return;
    }
    if (lat === null || lng === null || !locationConfirmed) {
      setError(t("addressForm.locationRequired"));
      return;
    }
    setError("");
    setStatus("saving");

    const token = getUserToken();
    if (!token) {
      setStatus("form");
      setError(t("addressForm.sessionError"));
      return;
    }

    const body = {
      customerAddressDescription: description.trim(),
      customerAddressNote: note.trim(),
      customerAddressIsDefault: isDefault,
      customerAddressLatitude: lat,
      customerAddressLongitude: lng,
      cityId,
      townId,
    };

    try {
      if (isEdit && address) {
        await updateCustomerAddress(address.id, body);
      } else {
        await addCustomerAddress(token.userId, body);
      }
      setStatus("success");
      setTimeout(() => {
        closeRef.current?.click();
        onSuccess();
      }, 3000);
    } catch {
      setStatus("form");
      setError(t("addressForm.error"));
    }
  };

  return (
    <div
      className="modal fade"
      id="adresimModal"
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header background-red">
            <h5 className="modal-title text-white">
              <i className={`bi ${isEdit ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
              {isEdit ? t("addressForm.editTitle") : t("addressForm.addTitle")}
            </h5>
            <button
              ref={closeRef}
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            />
          </div>

          <div className="modal-body px-4 py-3 position-relative">
            {status === "success" ? (
              <div className="text-center py-4">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "3rem" }}></i>
                <p className="mt-3 fw-semibold fs-5">{t("addressForm.saved")}</p>
                <p className="text-muted small">{t("common.windowClosing")}</p>
              </div>
            ) : (
              <>
                {status === "saving" && (
                  <div className="address-popup-overlay">
                    <div className="spinner-border text-danger" role="status"></div>
                    <p className="mt-2 text-muted small">{t("addressForm.saving")}</p>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger py-2 small mb-3">{error}</div>
                )}

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold mb-1">{t("addressForm.city")} <span className="text-danger">*</span></label>
                    <select
                      className="form-select form-select-sm"
                      value={cityId}
                      onChange={(e) => handleCityChange(e.target.value)}
                    >
                      <option value="">{t("addressForm.select")}</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>{c.cityNameTr}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold mb-1">{t("addressForm.district")} <span className="text-danger">*</span></label>
                    <select
                      className="form-select form-select-sm"
                      value={townId}
                      onChange={(e) => { setTownId(e.target.value); setLat(null); setLng(null); setLocationConfirmed(false); }}
                      disabled={!cityId}
                    >
                      <option value="">{t("addressForm.select")}</option>
                      {towns.map((t) => (
                        <option key={t.id} value={t.id}>{t.townNameTr}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold mb-1">{t("addressForm.addressTitle")} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("addressForm.addressTitlePlaceholder")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold mb-1">
                    {t("addressForm.addressDetail")}
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder={t("addressForm.addressDetailPlaceholder")}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>

                <div className="form-check form-switch address-default-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isDefaultSwitch"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="isDefaultSwitch">
                    {t("addressForm.setDefault")}
                  </label>
                </div>

                <hr className="my-2" />

                <label className="form-label small fw-semibold mb-2">
                  <i className="bi bi-map me-1 text-danger"></i>
                  {t("addressForm.selectLocation")}
                  <span className="text-danger ms-1">*</span>
                </label>
                {lat === null && (
                  <div className="alert alert-warning py-2 small mb-2">
                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                    {t("addressForm.locationHint")}
                  </div>
                )}
                {mapVisible ? (
                  <AddressMap
                    key={address?.id ?? "new"}
                    cityName={selectedCity?.cityNameTr ?? ""}
                    townName={selectedTown?.townNameTr ?? ""}
                    initialPos={lat !== null && lng !== null ? { lat, lng } : null}
                    onPinChange={(la, ln) => { setLat(la); setLng(ln); }}
                    onConfirmedChange={setLocationConfirmed}
                  />
                ) : (
                  <div
                    className="d-flex justify-content-center align-items-center bg-light rounded"
                    style={{ height: "280px" }}
                  >
                    <span className="spinner-border spinner-border-sm text-danger me-2" />
                    <span className="small text-muted">Harita yükleniyor...</span>
                  </div>
                )}
                {lat === null || lng === null || !locationConfirmed ? (
                  <div className="mt-2 p-2 bg-danger bg-opacity-10 border border-danger rounded small text-center text-danger">
                    <i className="bi bi-geo-alt me-1"></i>
                    {t("addressForm.locationNotSelected")}
                  </div>
                ) : null}
              </>
            )}
          </div>

          {status !== "success" && (
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">
                {t("common.close")}
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm px-4"
                onClick={handleSubmit}
                disabled={status === "saving" || !locationConfirmed}
              >
                {isEdit ? t("addressForm.editBtn") : t("addressForm.saveBtn")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdresimPopup;
